import React from 'react';
import { compose, map, find, propEq, sort, filter, includes } from 'ramda';
import {
  reduxForm,
  InjectedFormProps,
  Form,
  formValueSelector,
} from 'redux-form';
import Button from '@material-ui/core/Button';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import WizardCard from 'components/WizardCard/WizardCard';
import Fields from 'components/Fields';
import { PreviousStepType } from 'components/Wizard/Wizard';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import wizard from 'consts/wizard';
import IntlProvider, {
  useFormatMessage,
  IntlProviderType,
  getLabelByIntlPrefix,
} from 'components/IntlProvider';

import { validate, wizardStyles } from './wizardUtils';

const { connect, selectors } = StoreProvider;

interface SegmentType {
  id: number;
  value: string;
  label: string;
}

interface GenderType {
  id: number;
  value: string;
  label: string;
}

interface CategoryType {
  id: number;
  category: string;
  en_category: string;
}

interface CountryType {
  name: string;
  code: string;
}

interface GoalType {
  id: number;
  value: string;
  label: string;
}

interface SummaryProps extends PreviousStepType {
  subcategories: CategoryType[];
  category: CategoryType;
  genders: GenderType[];
  audienceAge: [number, number];
  countries: CountryType[];
  audienceGroupSize: string;
  segments: SegmentType[];
  goals: GoalType[];
  intl: IntlProviderType;
}

const Summary = ({
  handleSubmit,
  error,
  subcategories,
  category,
  previousStep,
  genders,
  audienceAge,
  countries,
  audienceGroupSize,
  segments,
  goals,
}: SummaryProps & InjectedFormProps) => {
  const classes = wizardStyles();
  const t = useFormatMessage();

  return (
    <Form onSubmit={handleSubmit} className={classes.form} noValidate>
      <Grid container justify="center" spacing={3}>
        <Grid item xs={12} lg={10}>
          <Grid item xs={12}>
            <Typography
              component="h1"
              variant="h3"
              color="inherit"
              className={classes.summary}
            >
              {t('forms.wizard.summary.title', 'Project Summary')}
            </Typography>
          </Grid>
        </Grid>
        <WizardCard xs={12} sm={9} md={8} lg={6}>
          <Grid item xs={12}>
            <Typography
              component="h1"
              variant="h5"
              color="inherit"
              className={classes.title}
            >
              {t('forms.wizard.summary.study', 'Name of your study')}
            </Typography>
            <Fields.Wizard.ProductInfo />
            <Typography
              component="h1"
              variant="h5"
              color="inherit"
              className={classes.title}
            >
              {t('forms.wizard.summary.category', 'Project category')}
            </Typography>
            {category.id !== wizard.OTHER_CATEGORY_ID ? (
              <Fields.Wizard.Subcategory
                category={category}
                subcategories={subcategories}
              />
            ) : (
              <Fields.Wizard.OtherCategory category={category} />
            )}
            <Typography
              component="h1"
              variant="h5"
              color="inherit"
              className={classes.title}
            >
              {t('forms.wizard.summary.audience', 'Audience')}
            </Typography>
            <Fields.Wizard.Gender genders={genders} />
            <Fields.Wizard.Age audienceAge={audienceAge} />
            <Fields.Wizard.Country countries={countries} />
            <Typography
              component="h1"
              variant="h5"
              color="inherit"
              className={classes.title}
            >
              {t('forms.wizard.summary.interviews', 'Number of interviews')}
            </Typography>
            <Fields.Wizard.GroupSize size={audienceGroupSize} />
            <Typography
              component="h1"
              variant="h5"
              color="inherit"
              className={classes.title}
            >
              {t('forms.wizard.summary.research', 'Research goal')}
            </Typography>
            <Fields.Wizard.ResearchGoal goals={goals} />
            <Fields.Wizard.Segments segments={segments} />
          </Grid>
        </WizardCard>
        <WizardCard xs={12} sm={3} md={4} lg={4}>
          <Grid item xs={12}>
            <Typography
              component="h1"
              variant="h5"
              color="inherit"
              className={classes.title}
            >
              {t(
                'forms.wizard.summary.estimatedcost',
                'Estimated cost for the indicated audience group'
              )}
            </Typography>
            <Fields.Wizard.Discount />
            {error && <FormHelperText error>{error}</FormHelperText>}
            <Button
              classes={{
                root: classes.submitButton,
              }}
              type="submit"
              variant="contained"
              color="primary"
            >
              {t('fields.wizard.common.save', 'Save project')}
            </Button>
            <Button
              onClick={previousStep}
              classes={{
                root: classes.secondaryButton,
              }}
              color="secondary"
            >
              {t('fields.wizard.common.back', 'Back')}
            </Button>
          </Grid>
        </WizardCard>
      </Grid>
    </Form>
  );
};

const wizardSelector = formValueSelector('wizard');

export default compose(
  IntlProvider.withIntl,
  connect((state, props: SummaryProps) => ({
    category: find(
      propEq('id', parseInt(wizardSelector(state, 'category'), 10))
    )(selectors.assistantSelectors.categories(state)),
    subcategories: compose(
      map((category: CategoryType) => ({
        ...category,
        value: category.id.toString(),
        label: getLabelByIntlPrefix(category, props.intl.locale, 'category'),
      })),
      sort((a: CategoryType, b: CategoryType) => a.id - b.id)
    )(selectors.assistantSelectors.subcategories(state)),
    genders: compose(
      map((gender: GenderType) => ({
        ...gender,
        value: gender.id.toString(),
        label: getLabelByIntlPrefix(gender, props.intl.locale, 'label'),
      })),
      sort((a: GenderType, b: GenderType) => a.id - b.id)
    )(selectors.assistantSelectors.genders(state)),
    audienceAge: wizardSelector(state, 'audience_age'),
    countries: compose(
      filter(({ code }: CountryType) =>
        includes(code, wizard.PERMITTED_COUNTRIES_CODE)
      )
    )(selectors.assistantSelectors.countries(state)),
    audienceGroupSize: wizardSelector(state, 'audience_group_size'),
    segments: compose(
      map((segment: SegmentType) => ({
        ...segment,
        value: segment.id.toString(),
        label: getLabelByIntlPrefix(segment, props.intl.locale, 'label'),
      })),
      sort((a: SegmentType, b: SegmentType) => a.id - b.id)
    )(selectors.assistantSelectors.segments(state)),
    goals: compose(
      map((gola: GoalType) => ({
        ...gola,
        value: gola.id.toString(),
        label: getLabelByIntlPrefix(gola, props.intl.locale, 'label'),
      })),
      sort((a: GoalType, b: GoalType) => a.id - b.id)
    )(selectors.assistantSelectors.goals(state)),
  })),
  reduxForm({
    form: 'wizard',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    validate,
  })
)(Summary);
